/**
 * MedicDashboardPage - Dashboard complet pentru medici
 * 
 * Afișează toate KPI-urile și analytics pentru un medic
 * Medici: văd DOAR propriile date
 * Admin/Operator: pot selecta orice medic
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ro } from 'date-fns/locale'
import { useAuth } from '../contexts/AuthContext'
import { useAnalytics } from '../hooks/useAnalytics'
import { PerformanceCard } from '../components/analytics/PerformanceCard'
import { ProductivityCard } from '../components/analytics/ProductivityCard'
import { ActivityCard } from '../components/analytics/ActivityCard'
import { ReportsStatsCard } from '../components/analytics/ReportsStatsCard'
import apiClient from '../services/api'

interface DoctorScore {
  score: number
  stars: number
  score_percentage: number
  breakdown: {
    base_score: number
    completion_bonus: number
    urgency_bonus: number
    pickup_penalty: number
    finalize_penalty: number
    unassign_penalty: number
    overdue_penalty: number
  }
}

const MedicDashboardPage: React.FC = () => {
  const { user } = useAuth()
  
  // State pentru medic selectat (admin/operator pot selecta, medici văd doar pe ei)
  // Pentru admin/operator, nu setăm ID-ul lor, ci așteptăm selecția
  const [selecteddoctorId, setSelecteddoctorId] = useState<string>(
    user?.role === 'medic' ? (user?.id || '') : ''
  )
  const [availableMedics, setAvailableMedics] = useState<Array<{ id: string; name: string }>>([])
  
  // State pentru doctor score (din endpoint vechi)
  const [doctorScore, setDoctorScore] = useState<DoctorScore | null>(null)
  const [scoreLoading, setScoreLoading] = useState(false)
  
  // Filters
  const [fromDate, setFromDate] = useState<Date | null>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
  const [toDate, setToDate] = useState<Date | null>(new Date())
  
  // Error & loading
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  // Hook pentru analytics
  const {
    productivity,
    activity,
    reportsStats,
    loading: analyticsLoading,
    error: analyticsError,
    fetchAll,
  } = useAnalytics({
    doctorId: selecteddoctorId,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    autoFetch: false, // Manual control
  })

  // Fetch medici disponibili (doar pentru admin/operator)
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operator') {
      fetchAvailableMedics()
    } else if (user?.role === 'medic') {
      // Pentru medici, setează ID-ul lor
      setSelecteddoctorId(user.id)
    }
  }, [user])

  // Fetch data când se schimbă medicul sau datele
  useEffect(() => {
    if (selecteddoctorId) {
      fetchDashboardData()
    }
  }, [selecteddoctorId, fromDate, toDate])

  const fetchAvailableMedics = async () => {
    try {
      const response = await apiClient.get('/users/?role=medic&limit=100')
      // API returns array directly, not wrapped in { users: [] }
      const medics = Array.isArray(response.data) ? response.data : []
      setAvailableMedics(
        medics.map((m: any) => {
          // Try to get name from doctor_profile first, then fallback to email
          let name = m.email
          if (m.doctor_profile) {
            const firstName = m.doctor_profile.first_name || ''
            const lastName = m.doctor_profile.last_name || ''
            if (firstName || lastName) {
              name = `${firstName} ${lastName}`.trim()
            }
          }
          return {
            id: m.id,
            name: name,
          }
        })
      )
    } catch (err) {
      console.error('Error fetching medics:', err)
    }
  }

  const fetchDoctorScore = async () => {
    if (!selecteddoctorId) return

    try {
      setScoreLoading(true)
      const params = new URLSearchParams()
      if (fromDate) params.append('from', fromDate.toISOString().split('T')[0])
      if (toDate) params.append('to', toDate.toISOString().split('T')[0])

      const response = await apiClient.get(
        `/scoring/doctor/${selecteddoctorId}${params.toString() ? `?${params.toString()}` : ''}`
      )
      setDoctorScore(response.data)
    } catch (err: any) {
      console.error('Error fetching doctor score:', err)
      setError(err.response?.data?.detail || 'Eroare la încărcarea scorului')
    } finally {
      setScoreLoading(false)
    }
  }

  const fetchDashboardData = async () => {
    setError(null)
    
    // Fetch score + analytics în paralel
    await Promise.all([
      fetchDoctorScore(),
      fetchAll(selecteddoctorId),
    ])
  }

  const handleRefresh = async () => {
    if (!selecteddoctorId) {
      setError('Vă rugăm să selectați un medic mai întâi')
      return
    }
    setRefreshing(true)
    await fetchDashboardData()
    setRefreshing(false)
  }

  // Verifică permisiuni
  if (!user || !['medic', 'admin', 'operator'].includes(user.role)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Nu aveți permisiuni pentru a accesa această pagină.
        </Alert>
      </Box>
    )
  }

  const loading = scoreLoading || analyticsLoading

  // Compact styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Box sx={{ p: 1.5 }}>
        {/* Header - Compact */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box display="flex" alignItems="center">
            <AssessmentIcon sx={{ fontSize: 24, mr: 1, color: 'primary.main' }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>Dashboard Performanță</Typography>
          </Box>
          <Button variant="outlined" startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={handleRefresh} disabled={refreshing || !selecteddoctorId} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>
            Refresh
          </Button>
        </Box>

        {/* Filters - Compact */}
        <Paper elevation={1} sx={{ p: 1.5, mb: 1.5 }}>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {(user.role === 'admin' || user.role === 'operator') && (
              <FormControl size="small" sx={{ minWidth: 180, ...inputSx }}>
                <InputLabel>Medic</InputLabel>
                <Select value={selecteddoctorId} onChange={(e) => setSelecteddoctorId(e.target.value)} label="Medic">
                  {availableMedics.map((medic) => (<MenuItem key={medic.id} value={medic.id} sx={{ fontSize: '0.8rem' }}>{medic.name}</MenuItem>))}
                </Select>
              </FormControl>
            )}
            <DatePicker label="De la" value={fromDate} onChange={(newValue) => setFromDate(newValue)} slotProps={{ textField: { size: 'small', sx: { width: 140, ...inputSx } } }} />
            <DatePicker label="Până la" value={toDate} onChange={(newValue) => setToDate(newValue)} slotProps={{ textField: { size: 'small', sx: { width: 140, ...inputSx } } }} />
          </Box>
        </Paper>

        {/* Error Display */}
        {(error || analyticsError) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || analyticsError}
          </Alert>
        )}

        {/* No Medic Selected (Admin/Operator) */}
        {!selecteddoctorId && (user.role === 'admin' || user.role === 'operator') && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Vă rugăm să selectați un medic din lista de mai sus pentru a vizualiza dashboard-ul de performanță.
          </Alert>
        )}

        {/* Loading State */}
        {loading && !productivity && !activity && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress size={60} />
          </Box>
        )}

        {/* Dashboard Content - Fixed Height Cards */}
        {!loading && (productivity || activity || reportsStats || doctorScore) && (
          <Grid container spacing={1.5} alignItems="stretch">
            {/* Row 1: Performance + Productivity */}
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', height: 280 }}>
                <PerformanceCard
                  score={doctorScore?.score || 0}
                  stars={doctorScore?.stars || 0}
                  scorePercentage={doctorScore?.score_percentage}
                  breakdown={doctorScore?.breakdown}
                  loading={scoreLoading}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', height: 280 }}>
                <ProductivityCard
                  totalStudies={productivity?.total_studies || 0}
                  completedStudies={productivity?.completed_studies || 0}
                  totalImages={productivity?.total_images_read || 0}
                  complexityWeightedVolume={productivity?.complexity_weighted_volume || 0}
                  studiesPerHour={productivity?.studies_per_hour || 0}
                  imagesPerHour={productivity?.images_per_hour || 0}
                  modalityDistribution={productivity?.modality_distribution}
                  priorityDistribution={productivity?.priority_distribution}
                  loading={analyticsLoading && !productivity}
                />
              </Box>
            </Grid>

            {/* Row 2: Activity + Reports */}
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', height: 220 }}>
                <ActivityCard
                  loginCount={activity?.login_count || 0}
                  totalLoggedTimeSec={activity?.total_logged_time_sec || 0}
                  totalActiveTimeSec={activity?.total_active_time_sec || 0}
                  idleTimeSec={activity?.idle_time_sec || 0}
                  activeSessionsCount={activity?.active_sessions_count || 0}
                  peakHour={activity?.peak_hour}
                  lastActivityAt={activity?.last_activity_at}
                  loading={analyticsLoading && !activity}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', height: 220 }}>
                <ReportsStatsCard
                  totalReports={reportsStats?.total_reports || 0}
                  draftCount={reportsStats?.draft_count || 0}
                  finalizedCount={reportsStats?.finalized_count || 0}
                  avgLengthChars={reportsStats?.avg_length_chars || 0}
                  avgWordCount={reportsStats?.avg_word_count || 0}
                  avgWritingTimeSec={reportsStats?.avg_writing_time_sec || 0}
                  wordsPerMinute={reportsStats?.words_per_minute || 0}
                  avgTimeToFinalizeSec={reportsStats?.avg_time_to_finalize_sec || 0}
                  postFinalizeEditsCount={reportsStats?.post_finalize_edits_count || 0}
                  loading={analyticsLoading && !reportsStats}
                />
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Empty State */}
        {!loading && !productivity && !activity && !reportsStats && !doctorScore && (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nu există date disponibile pentru perioada selectată
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Selectați altă perioadă sau verificați că există studii alocate.
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default MedicDashboardPage

